import clsx from "clsx";
import { useIntl } from "react-intl";

import { messages } from "@/localization/Shared.messages";

import type { CSSProperties } from "react";

type MediaItemPosterImage = {
  src: string;
  width?: number;
  height?: number;
};

export type MediaItemData = {
  src?: string;
  videoEmbedUrl?: string;
  posterImage?: MediaItemPosterImage;
  alt: string;
  width?: number;
  height?: number;
  contentType?: string;
  keyline?: boolean;
  ratio?: string;
  ratioMobile?: string;
  captionAlignment?: "content-grid" | "media-width";
  centerCaption?: boolean;
  cmsName?: string;
  copyToClipboard?: boolean;
  showVideoControls?: boolean;
  truncateCaption?: boolean;
  useVimeoControls?: boolean;
  videoAutoPlay?: boolean;
  videoLoop?: boolean;
  videoShowMuteButton?: boolean;
};

export type MediaItemProps = MediaItemData & {
  className?: string;
  imageClassName?: string;
};

const keylineStyle = {
  borderColor: "light-dark(#0000001f, #ffffff33)",
};

function normalizeAspectRatio(ratio: string | undefined) {
  return ratio?.replace(":", "/");
}

export function MediaItem({
  src,
  videoEmbedUrl,
  posterImage,
  alt,
  width,
  height,
  keyline = false,
  ratio,
  ratioMobile,
  className,
  imageClassName,
  showVideoControls = true,
  useVimeoControls = false,
  videoAutoPlay = false,
  videoLoop = false,
  cmsName,
}: MediaItemProps) {
  const intl = useIntl();
  const aspectRatio = normalizeAspectRatio(ratio);
  const aspectRatioMobile = normalizeAspectRatio(ratioMobile) ?? aspectRatio;
  const hasAspectRatio = Boolean(aspectRatio || aspectRatioMobile);
  const aspectRatioStyle =
    hasAspectRatio ?
      ({
        ...(aspectRatio ? { "--media-item-aspect-ratio": aspectRatio } : undefined),
        ...(aspectRatioMobile ?
          { "--media-item-aspect-ratio-mobile": aspectRatioMobile }
        : undefined),
      } as CSSProperties)
    : undefined;

  return (
    <div
      className={clsx(
        "overflow-hidden",
        hasAspectRatio &&
          "aspect-(--media-item-aspect-ratio-mobile) md:aspect-(--media-item-aspect-ratio)",
        videoEmbedUrl && posterImage && "relative",
        className,
        keyline && "border",
      )}
      style={{
        ...aspectRatioStyle,
        ...(keyline ? keylineStyle : undefined),
      }}
    >
      {videoEmbedUrl && posterImage ?
        <img
          src={posterImage.src}
          alt=""
          width={posterImage.width}
          height={posterImage.height}
          className="absolute inset-0 size-full object-cover"
        />
      : null}
      {videoEmbedUrl ?
        <iframe
          src={buildEmbeddedVideoUrl({
            videoEmbedUrl,
            showVideoControls,
            useVimeoControls,
            videoAutoPlay,
            videoLoop,
          })}
          title={cmsName || alt || intl.formatMessage(messages.embeddedVideo)}
          className={clsx("size-full border-0", posterImage && "relative")}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      : src ?
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          className={clsx("size-full object-cover", imageClassName)}
        />
      : null}
    </div>
  );
}

function buildEmbeddedVideoUrl({
  videoEmbedUrl,
  showVideoControls,
  useVimeoControls,
  videoAutoPlay,
  videoLoop,
}: Pick<
  MediaItemData,
  "showVideoControls" | "useVimeoControls" | "videoAutoPlay" | "videoEmbedUrl" | "videoLoop"
>): string {
  const normalizedUrl = videoEmbedUrl?.replaceAll("&amp;", "&");

  if (!normalizedUrl) {
    return "";
  }

  try {
    const url = new URL(normalizedUrl);

    if (!url.hostname.endsWith("vimeo.com")) {
      return normalizedUrl;
    }

    url.searchParams.set("autopause", "0");
    url.searchParams.set("controls", useVimeoControls || showVideoControls ? "1" : "0");

    if (videoAutoPlay) {
      url.searchParams.set("autoplay", "1");
      url.searchParams.set("muted", "1");
    }

    if (videoLoop) {
      url.searchParams.set("loop", "1");
    }

    return url.toString();
  } catch {
    return normalizedUrl;
  }
}
